import { useCallback, useEffect, useState } from "react";
import {
  WaveCloudAPI,
  waveSpaceDisplayTitle,
  type WaveOceanSpaceRecord,
  type WaveSpaceMember,
  type WaveSpaceMemberRole,
} from "@vida/wave-core";
import { createWaveApiConfig } from "../../services/waveSession";
import { currentUserSub } from "../../auth";

type Props = {
  space: WaveOceanSpaceRecord;
  onClose: () => void;
  onError: (message: string) => void;
  onMembersChanged: () => void;
};

const ASSIGNABLE_ROLES: WaveSpaceMemberRole[] = ["EDITOR", "VIEWER"];

const ROLE_LABEL: Record<WaveSpaceMemberRole, string> = {
  OWNER: "Proprietario",
  EDITOR: "Editor",
  VIEWER: "Lettore",
};

/**
 * Pannello membri di uno spazio OceanSpace (W3): elenco, invito per email,
 * cambio ruolo, rimozione. Riusa i resolver `ocean-spaces` condivisi. Le azioni
 * sono disponibili solo al proprietario e mai sugli spazi riservati (Open Seas).
 */
export function WaveSpaceMembersPanel({
  space,
  onClose,
  onError,
  onMembersChanged,
}: Props) {
  const [members, setMembers] = useState<WaveSpaceMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<WaveSpaceMemberRole>("EDITOR");
  const [busy, setBusy] = useState(false);
  const me = currentUserSub();

  const loadMembers = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await WaveCloudAPI.listSpaceMembers(
        createWaveApiConfig(),
        space.spaceId,
      );
      setMembers(rows);
    } catch (e) {
      onError(e instanceof Error ? e.message : "Impossibile caricare i membri");
    } finally {
      setLoading(false);
    }
  }, [space.spaceId, onError]);

  useEffect(() => {
    void loadMembers();
  }, [loadMembers]);

  const addMember = useCallback(async () => {
    const normalized = email.trim().toLowerCase();
    if (!normalized.includes("@")) {
      onError("Inserisci un indirizzo email valido.");
      return;
    }
    setBusy(true);
    try {
      await WaveCloudAPI.addSpaceMember(
        createWaveApiConfig(),
        space.spaceId,
        normalized,
        role,
      );
      setEmail("");
      await loadMembers();
      onMembersChanged();
    } catch (e) {
      onError(e instanceof Error ? e.message : "Invito non riuscito");
    } finally {
      setBusy(false);
    }
  }, [email, role, space.spaceId, loadMembers, onMembersChanged, onError]);

  const changeRole = useCallback(
    async (member: WaveSpaceMember, nextRole: WaveSpaceMemberRole) => {
      if (nextRole === member.role) return;
      setBusy(true);
      try {
        await WaveCloudAPI.updateSpaceMemberRole(
          createWaveApiConfig(),
          space.spaceId,
          member.sub,
          nextRole,
        );
        await loadMembers();
        onMembersChanged();
      } catch (e) {
        onError(e instanceof Error ? e.message : "Cambio ruolo non riuscito");
      } finally {
        setBusy(false);
      }
    },
    [space.spaceId, loadMembers, onMembersChanged, onError],
  );

  const removeMember = useCallback(
    async (member: WaveSpaceMember) => {
      setBusy(true);
      try {
        await WaveCloudAPI.removeSpaceMember(
          createWaveApiConfig(),
          space.spaceId,
          member.sub,
        );
        await loadMembers();
        onMembersChanged();
      } catch (e) {
        onError(e instanceof Error ? e.message : "Rimozione non riuscita");
      } finally {
        setBusy(false);
      }
    },
    [space.spaceId, loadMembers, onMembersChanged, onError],
  );

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="modal-card wave-members-card"
        role="dialog"
        aria-modal="true"
        aria-label={`Membri di ${waveSpaceDisplayTitle(space)}`}
        onClick={(e) => e.stopPropagation()}
      >
        <h3>Membri · {waveSpaceDisplayTitle(space)}</h3>

        <div className="wave-members-invite">
          <input
            type="email"
            value={email}
            placeholder="email@esempio.com"
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void addMember();
            }}
            disabled={busy}
          />
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as WaveSpaceMemberRole)}
            disabled={busy}
            aria-label="Ruolo del nuovo membro"
          >
            {ASSIGNABLE_ROLES.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABEL[r]}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="primary-btn"
            disabled={busy || !email.trim()}
            onClick={() => void addMember()}
          >
            Invita
          </button>
        </div>

        {loading ? (
          <p className="muted">Caricamento membri…</p>
        ) : members.length === 0 ? (
          <p className="muted">Ancora nessun membro. Invita qualcuno per collaborare.</p>
        ) : (
          <ul className="wave-members-list">
            {members.map((member) => {
              const isOwnerRow = member.role === "OWNER";
              const isSelf = member.sub === me;
              return (
                <li key={member.sub} className="wave-members-row">
                  <span className="wave-members-email">
                    {member.email ?? member.sub}
                    {isSelf && <span className="muted"> · tu</span>}
                  </span>
                  {isOwnerRow ? (
                    <span className="wave-members-role muted">{ROLE_LABEL.OWNER}</span>
                  ) : (
                    <>
                      <select
                        value={member.role}
                        disabled={busy}
                        onChange={(e) =>
                          void changeRole(member, e.target.value as WaveSpaceMemberRole)
                        }
                        aria-label={`Ruolo di ${member.email ?? member.sub}`}
                      >
                        {ASSIGNABLE_ROLES.map((r) => (
                          <option key={r} value={r}>
                            {ROLE_LABEL[r]}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="danger-btn"
                        disabled={busy}
                        onClick={() => void removeMember(member)}
                      >
                        Rimuovi
                      </button>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        <div className="modal-actions">
          <button type="button" onClick={onClose}>
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
}
